import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Drawer,
  Tooltip,
} from "@mui/material";
import { DoubleArrow, Edit, FileCopy, Delete } from "@mui/icons-material";
import { EditShiftGradePanel } from "./EditShiftGradePanel";
import { CloneShiftGradePanel } from "./CloneShiftGradePanel";
import { tooltipStyleProps } from "services/constants/Styles";
import { themes } from "services/constants";
import ShiftGradePreview from "./ShiftGradePreview";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";

export const ViewShiftGradePanel = ({
  onClose,
  editShiftGradeDetails,
  reCallApi,
  setHandleLoader,
  setGetId,
  setIsViewPanelOpen,
  screenAccess,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isClonePanelOpen, setIsClonePanelOpen] = useState(false);

  const handleEditShiftGrade = () => {
    setIsEditPanelOpen(true);
  };

  const handleEditClosePanel = () => {
    setIsEditPanelOpen(false);
  };

  const handleCloseShiftPanel = () => {
    setIsClonePanelOpen(false);
  };

  const handleCloneShiftPanel = () => {
    setIsClonePanelOpen(true);
  };

  const handleCloseClonePanel = () => {
    setIsClonePanelOpen(false);
    setIsViewPanelOpen(false);
  };

  const formatWeekoffPreview = (weekoffItem) => {
    if (!weekoffItem) return "";

    const { week_occurrence, day, type } = weekoffItem;

    const suffix = ["th", "st", "nd", "rd"];
    const selectedWeeks =
      week_occurrence
        ?.map((n) => {
          const v = n % 100;
          return n + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
        })
        .join(" & ") || "";

    return `${selectedWeeks} - ${day} ${type}`;
  };

  const weekoffValue = editShiftGradeDetails?.shift_grade_week_off
    .map(formatWeekoffPreview)
    .join("\n");

  const handleDeleteSftGrade = async () => {
    const response = await customAPI(
      `${domain}administration/shiftroster/shiftgrade/shift-grades/${editShiftGradeDetails?.id}`,
      "PUT",
      {
        name: editShiftGradeDetails?.name.trim(),
        shift_hours: editShiftGradeDetails?.shift_hours,
        shift_grade_week_off: editShiftGradeDetails?.shift_grade_week_off,
        isdeleted: true,
      }
    );
    if (response.ok) {
      showToast("success", "Shift grade details deleted successfully");
      reCallApi(true);
      setHandleLoader(true);
      onClose();
    }
  };

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <IconButton
                onClick={onClose}
                sx={{
                  color: themes?.grayColor,
                  "&:hover": { backgroundColor: themes?.primary },
                }}
              >
                <DoubleArrow />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "50%",
                  padding: "4px 4px",
                  gap: 0.5,
                }}
              >
                {editShiftGradeDetails?.is_editable && (
                  <Tooltip title="Delete" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={handleDeleteSftGrade}
                      sx={{
                        color: themes?.redColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                        mt: 0.5,
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Clone" arrow slotProps={tooltipStyleProps}>
                  <IconButton
                    onClick={() => handleCloneShiftPanel()}
                    sx={{
                      color: themes?.blueColor,
                      backgroundColor: themes?.primary,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: themes?.primary },
                    }}
                  >
                    <FileCopy />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                  <IconButton
                    onClick={() => {
                      if (screenAccess) {
                        handleEditShiftGrade();
                      } else {
                        showToast(
                          "error",
                          `Access denied: You are not authorized to edit shift grade`
                        );
                      }
                    }}
                    sx={{
                      color: themes?.greenColor,
                      backgroundColor: themes?.primary,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: themes?.primary },
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ ml: 2.2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#111827", textAlign: "center" }}
              >
                {editShiftGradeDetails?.name}
              </Typography>
            </Box>
          </Box>
        </Paper>
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "white",
              border: `1px solid ${themes?.miliedLightGray}`,
              borderRadius: 2,
            }}
          >
            <ShiftGradePreview
              shiftName={editShiftGradeDetails?.name}
              weekoffValue={weekoffValue}
              shiftDuration={editShiftGradeDetails?.shift_hours}
              IsViewScreen={true}
            />
          </Paper>
        </Box>
      </Box>
      <Drawer
        anchor="right"
        open={isEditPanelOpen || isClonePanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          isEditPanelOpen ? handleEditClosePanel() : handleCloseShiftPanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.whiteColor,
          },
        }}
      >
        {isEditPanelOpen ? (
          <EditShiftGradePanel
            onClose={handleEditClosePanel}
            editShiftGradeDetails={editShiftGradeDetails}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            setGetId={setGetId}
          />
        ) : isClonePanelOpen ? (
          <CloneShiftGradePanel
            onClose={handleCloseShiftPanel}
            editShiftGradeDetails={editShiftGradeDetails}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            handleClose={handleCloseClonePanel}
          />
        ) : null}
      </Drawer>
    </>
  );
};
